import type { MenuItem, Rect } from "../interop/models";
import { defineStore } from "pinia";
import { ref } from "vue";
import { noopWithWarn } from "@/common/lib/function";

export const useContextMenuStore = defineStore("contextMenu", () => {
  const visible = ref(false);

  const menu = ref<MenuItem>();
  const rect = ref<Rect>();
  const addToLibraryRequested = ref(false);

  function show(menuItem: MenuItem, menuRect: Rect) {
    // Workaround for A9-5857.
    // Intentionally delay the menu showing and try show it in the next frame.
    // This workaround is required to prevent displayed menu from receiving focus before "contextmenu" event is fired and handled.
    // Otherwise "contextmenu" event will be fired on the menu itself and not on the canvas,
    // causing both native and custom context menus to be displayed at the same time
    // This issue is present on Windows, but not on MacOS. Though, it is better to have the same behavior on all platforms.
    requestAnimationFrame(() => {
      menu.value = menuItem;
      rect.value = menuRect;
      visible.value = true;
    });
  }

  function hide() {
    visible.value = false;
  }

  const invokeMenuAction = ref<(id: number, value: boolean) => void>(noopWithWarn("Problem invoking context menu action. Callback to invoke context menu actions not configured"));

  function bindAction(value: (id: number, value: boolean) => void) {
    invokeMenuAction.value = value;
  }

  const invokeDestroy = ref(noopWithWarn("Problem invoking context menu destroy. Callback to invoke context menu actions not configured"));
  function bindDestroy(value: () => void) {
    invokeDestroy.value = value;
  }

  function destroy() {
    invokeDestroy?.value();
    menu.value = undefined;
    hide();
  }

  return {
    visible,
    menu,
    rect,
    addToLibraryRequested,
    invokeMenuAction,
    show,
    hide,
    destroy,
    bindAction,
    bindDestroy,
  };
});

export function show(menuJson: string, rectJson: string) {
  const store = useContextMenuStore();
  const menu = JSON.parse(menuJson) as MenuItem;
  const rect = JSON.parse(rectJson) as Rect;
  void store.show(menu, rect);
}

export function bindAction(invokeAction: (id: number, value: boolean) => void) {
  const store = useContextMenuStore();
  store.bindAction(invokeAction);
}

export function bindDestroy(invokeDestroy: () => void) {
  const store = useContextMenuStore();
  store.bindDestroy(invokeDestroy);
}

export function addToLibraryRequested() {
  const store = useContextMenuStore();
  store.addToLibraryRequested = true;
}
